import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { ImageUp, Save, Trash2, X } from 'lucide-react';
import { Botao, Cartao, Selo } from '../componentes/base';
import { Entrada, Rotulo } from '../componentes/formulario';
import { Confirmar, Modal } from '../componentes/sobreposicao';
import { RecorteImagem, carregarImagem, exportarRecorte, type ImagemCarregada, type ResultadoRecorte } from '../componentes/recorte-imagem';
import { anunciarOcultos } from '../componentes/sensivel';
import { useToast } from '../componentes/toast';
import { api, del, get, mensagemDeErro, post } from '../nucleo/api';
import { dataHora, email, nomeDeUsuario } from '../nucleo/formato';
import { useSessao } from '../nucleo/sessao';

/**
 * Lista de 30/10, itens 6 e 11 (Minha conta > Perfil).
 *  - `FotoPerfilAcoes` (item 6): trocar a foto (escolhe o arquivo, ajusta no recorte quadrado com guia circular, vê o resultado e
 *    salva) e remover (volta às iniciais). O servidor confere e reprocessa; aqui só se evita abrir o que não é PNG, JPG ou WebP.
 *  - `CartaoEntrada` (item 11): com que a pessoa entra (e-mail, nome de usuário e senha, o que valer para a conta), a troca de
 *    senha (pela própria pessoa, em Segurança) e o PEDIDO de troca do e-mail ou do nome de usuário, que o administrador aprova.
 *    Quem troca direto (rl30-3, `admin.usuarios` em todo workspace da conta) é levado a Segurança, sem o pedido que ninguém
 *    decidiria (a Dona, o único administrador).
 */

const LIMITE_ABRIR = 20 * 1024 * 1024;
const TIPOS_ACEITOS = ['image/png', 'image/jpeg', 'image/webp'];

/** Ferramentas trocáveis nos testes (o DOM simulado não decodifica imagem nem tem canvas). */
export const ferramentasFoto = { carregar: carregarImagem, exportar: exportarRecorte };

interface Pendente extends ResultadoRecorte {
  url: string;
}

export function FotoPerfilAcoes() {
  const { t } = useTranslation();
  const s = useSessao();
  const toast = useToast();
  const entrada = useRef<HTMLInputElement>(null);
  const [aberta, setAberta] = useState<ImagemCarregada | null>(null);
  const [pendente, setPendente] = useState<Pendente | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [confirmar, setConfirmar] = useState(false);
  const temFoto = !!s.usuario.avatar_versao;
  useEffect(() => () => aberta?.liberar(), [aberta]);
  useEffect(() => () => (pendente ? URL.revokeObjectURL(pendente.url) : undefined), [pendente]);

  const salvar = useMutation({
    mutationFn: (p: Pendente) => {
      const f = new FormData();
      const ext = p.blob.type === 'image/webp' ? 'webp' : p.blob.type === 'image/png' ? 'png' : 'jpg';
      f.append('arquivo', new File([p.blob], `foto.${ext}`, { type: p.blob.type }));
      return api('POST', '/nucleo/avatar', f);
    },
    onSuccess: async () => {
      setPendente(null);
      await s.recarregar();
      toast.sucesso(t('conta.avatar.salva'));
    },
    onError: (e) => setErro(mensagemDeErro(e)),
  });
  const remover = useMutation({
    mutationFn: () => del('/nucleo/avatar'),
    onSuccess: async () => {
      setConfirmar(false);
      await s.recarregar();
      toast.sucesso(t('conta.avatar.removida'));
    },
    onError: (e) => {
      setConfirmar(false);
      toast.erro(mensagemDeErro(e));
    },
  });

  const escolher = async (arquivo: File | undefined) => {
    if (entrada.current) entrada.current.value = '';
    if (!arquivo) return;
    setErro(null);
    if (!TIPOS_ACEITOS.includes(arquivo.type)) return setErro(t('conta.avatar.tipoInvalido'));
    if (arquivo.size > LIMITE_ABRIR) return setErro(t('conta.avatar.grandeDemais'));
    try {
      setAberta(await ferramentasFoto.carregar(arquivo));
    } catch {
      setErro(t('conta.avatar.naoAbriu'));
    }
  };
  const concluir = (r: ResultadoRecorte) => {
    setPendente({ ...r, url: URL.createObjectURL(r.blob) });
    setAberta(null);
  };

  return (
    <div className="flex min-w-0 flex-col gap-2" data-foto-perfil>
      <input
        ref={entrada}
        type="file"
        accept="image/png,image/jpeg,image/webp,.png,.jpg,.jpeg,.webp"
        className="sr-only"
        tabIndex={-1}
        title={t('conta.avatar.escolher')}
        onChange={(e) => void escolher(e.target.files?.[0])}
        data-foto-arquivo
      />
      {pendente && (
        <div className="flex min-w-0 items-center gap-3 rounded-lg border border-borda p-3" data-foto-pendente>
          <img src={pendente.url} alt={t('conta.avatar.resultadoAlt')} className="size-16 shrink-0 rounded-full border border-borda object-cover" />
          <p className="min-w-0 text-sm text-tinta-2">{t('conta.avatar.resultado')}</p>
        </div>
      )}
      {salvar.isPending && (
        <p role="status" className="text-sm text-tinta-2">
          {t('conta.avatar.processando')}
        </p>
      )}
      {erro && (
        <p role="alert" className="text-sm text-perigo" data-foto-erro>
          {erro}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        {pendente ? (
          <>
            <Botao variante="fantasma" icone={<X className="size-4" aria-hidden />} disabled={salvar.isPending} onClick={() => setPendente(null)} data-foto-descartar>
              {t('conta.avatar.descartar')}
            </Botao>
            <Botao icone={<Save className="size-4" aria-hidden />} carregando={salvar.isPending} onClick={() => salvar.mutate(pendente)} data-foto-salvar>
              {t('conta.avatar.salvar')}
            </Botao>
          </>
        ) : (
          <>
            <Botao variante="secundario" icone={<ImageUp className="size-4" aria-hidden />} onClick={() => entrada.current?.click()} data-foto-escolher>
              {temFoto ? t('conta.avatar.trocar') : t('conta.avatar.escolher')}
            </Botao>
            {temFoto && (
              <Botao variante="fantasma" icone={<Trash2 className="size-4" aria-hidden />} onClick={() => setConfirmar(true)} data-foto-remover>
                {t('conta.avatar.remover')}
              </Botao>
            )}
          </>
        )}
      </div>
      <Modal aberto={!!aberta} aoFechar={() => setAberta(null)} titulo={t('conta.avatar.ajustar')}>
        {aberta && <RecorteImagem imagem={aberta} modo="avatar" aoConcluir={concluir} aoCancelar={() => setAberta(null)} exportar={ferramentasFoto.exportar} />}
      </Modal>
      <Confirmar aberto={confirmar} aoFechar={() => setConfirmar(false)} aoConfirmar={() => remover.mutate()} titulo={t('conta.avatar.removerTitulo')} texto={t('conta.avatar.removerTexto')} carregando={remover.isPending} />
    </div>
  );
}

// ---------------------------------------------------------------------------------------------------------------------
// Entra com + pedido de troca

type TipoPedido = 'email' | 'usuario';
export interface MeuPedido {
  id: string;
  tipo: TipoPedido;
  valor_novo: string;
  status: 'pendente' | 'aprovado' | 'recusado';
  motivo_recusa: string | null;
  decidido_em: string | null;
  created_at: string;
}
export const CHAVE_MEUS_PEDIDOS = ['/auth/pedidos-acesso/meus'];
/**
 * Os pedidos da pessoa e se ela troca o próprio e-mail direto (review rl30-3, SPEC-M1 e SEG-B1: só com `admin.usuarios` em todo
 * workspace da conta, decidido pela API). Quem troca direto faz isso em Segurança e não recebe no Perfil a oferta do pedido.
 */
export interface MeusPedidos {
  itens: MeuPedido[];
  troca_direta?: { email: boolean; usuario?: boolean };
  /** Review rl30-4 (SPEC-M2): falso quando ninguém aqui decidiria o pedido (conta também em outro workspace). */
  pedido_possivel?: boolean;
}
export function useMeusPedidos() {
  return useQuery({ queryKey: CHAVE_MEUS_PEDIDOS, queryFn: () => get<MeusPedidos>('/auth/pedidos-acesso/meus') });
}

const valorDoPedido = (tipo: TipoPedido, valor: string) => anunciarOcultos(tipo === 'email' ? email(valor) : nomeDeUsuario(valor));

export function CartaoEntrada() {
  const { t } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const u = s.usuario;
  const semEmail = !!u.sem_email || !u.email;
  const porEmail = (u.login_email ?? true) && !semEmail;
  const porUsuario = u.login_usuario ?? true;
  const forma = porEmail && porUsuario ? 'ambos' : porEmail ? 'email' : 'usuario';
  const [pedindo, setPedindo] = useState<TipoPedido | null>(null);
  const [valor, setValor] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const meus = useMeusPedidos();
  const itens = meus.data?.itens ?? [];
  const emailDireto = !!meus.data?.troca_direta?.email;
  const usuarioDireto = !!meus.data?.troca_direta?.usuario;
  // Review rl30-4: sem quem decida (SPEC-M2), o aviso no lugar dos botões; carregando (visual B2), nem texto nem botões.
  const semDecisor = meus.data?.pedido_possivel === false;
  const carregandoTroca = meus.isPending;
  const pendente = (tipo: TipoPedido) => itens.find((p) => p.tipo === tipo && p.status === 'pendente');

  const abrir = (tipo: TipoPedido) => {
    setPedindo(tipo);
    setValor('');
    setErro(null);
  };
  const pedir = useMutation({
    mutationFn: () => post('/auth/pedidos-acesso', { tipo: pedindo, valor }),
    onSuccess: async () => {
      setPedindo(null);
      await qc.invalidateQueries({ queryKey: CHAVE_MEUS_PEDIDOS });
      toast.sucesso(t('conta.entrada.pedidoEnviado'));
    },
    onError: (e) => setErro(mensagemDeErro(e)),
  });
  const cancelar = useMutation({
    mutationFn: (id: string) => del(`/auth/pedidos-acesso/${id}`),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: CHAVE_MEUS_PEDIDOS });
      toast.sucesso(t('conta.entrada.pedidoCancelado'));
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  function enviar(e: FormEvent) {
    e.preventDefault();
    setErro(null);
    if (!valor.trim()) return setErro(t('erros.obrigatorio'));
    if (pedindo === 'email' && !valor.includes('@')) return setErro(t('conta.seguranca.acesso.emailInvalido'));
    pedir.mutate();
  }
  // Os pedidos já decididos que ainda interessam: o último de cada tipo, recusado (com o motivo) ou aprovado.
  const decididos = (['email', 'usuario'] as const).map((tipo) => itens.find((p) => p.tipo === tipo && p.status !== 'pendente')).filter((p): p is MeuPedido => !!p);

  return (
    <Cartao titulo={t('conta.entrada.titulo')}>
      <div className="flex min-w-0 flex-col gap-4" data-conta-entrada>
        <p className="text-sm text-tinta-1" data-entra-com={forma}>
          <span className="font-medium">{t('conta.entrada.entraCom')}</span> {t(`conta.entrada.formas.${forma}`)}
        </p>
        <dl className="grid gap-3 sm:grid-cols-2">
          <div className="min-w-0">
            <dt className="text-xs text-tinta-3">{t('conta.seguranca.acesso.usuario')}</dt>
            <dd className="numero text-sm break-all text-tinta-1">{u.usuario ? anunciarOcultos(nomeDeUsuario(u.usuario)) : '-'}</dd>
          </div>
          <div className="min-w-0">
            <dt className="text-xs text-tinta-3">{t('conta.seguranca.acesso.email')}</dt>
            <dd className="text-sm break-all text-tinta-1">{semEmail ? t('conta.seguranca.acesso.semEmail') : anunciarOcultos(email(u.email))}</dd>
          </div>
        </dl>
        <p className="text-sm text-tinta-3">
          {t('conta.entrada.senhaTexto')}{' '}
          <Link to="/conta/seguranca" className="font-medium text-acento-forte underline" data-entrada-senha>
            {t('conta.entrada.senhaLink')}
          </Link>
        </p>
        {!carregandoTroca && (
          <p className="text-sm text-tinta-3" data-entrada-troca={emailDireto && usuarioDireto ? 'direta' : emailDireto ? 'email-direto' : semDecisor ? 'sem-decisor' : 'pedido'}>
            {emailDireto ? (
              <>
                {usuarioDireto ? t('conta.entrada.trocaTextoDiretaTudo') : t('conta.entrada.trocaTextoDireta')}{' '}
                <Link to="/conta/seguranca" className="font-medium text-acento-forte underline" data-entrada-email-direto>
                  {usuarioDireto ? t('conta.entrada.acessoLink') : t('conta.entrada.emailLink')}
                </Link>
              </>
            ) : semDecisor ? (
              t('conta.entrada.semDecisor')
            ) : (
              t('conta.entrada.trocaTexto')
            )}
          </p>
        )}

        {itens.filter((p) => p.status === 'pendente').map((p) => (
          <div key={p.id} className="flex min-w-0 flex-wrap items-center justify-between gap-2 rounded-lg border border-borda bg-sup-2 px-3 py-2" data-pedido-pendente={p.tipo}>
            <p className="min-w-0 text-sm text-tinta-2">
              <Selo cor="dourado">{t('conta.entrada.aguardando')}</Selo> {t(`conta.entrada.pedido.${p.tipo}`, { valor: valorDoPedido(p.tipo, p.valor_novo) })}
            </p>
            <Botao variante="fantasma" carregando={cancelar.isPending && cancelar.variables === p.id} onClick={() => cancelar.mutate(p.id)} data-pedido-cancelar>
              {t('conta.entrada.cancelarPedido')}
            </Botao>
          </div>
        ))}
        {decididos.map((p) => (
          <p key={p.id} className="rounded-lg border border-borda px-3 py-2 text-sm text-tinta-2" data-pedido-decidido={p.status}>
            <Selo cor={p.status === 'aprovado' ? 'verde' : 'neutro'}>{t(`conta.entrada.status.${p.status}`)}</Selo> {t(`conta.entrada.pedido.${p.tipo}`, { valor: valorDoPedido(p.tipo, p.valor_novo) })}
            {p.decidido_em ? ` (${dataHora(p.decidido_em)})` : ''}
            {p.status === 'recusado' && p.motivo_recusa ? <span className="mt-1 block text-tinta-3">{t('conta.entrada.motivo', { motivo: p.motivo_recusa })}</span> : null}
          </p>
        ))}

        <div className="flex flex-wrap justify-end gap-2">
          {!carregandoTroca && !emailDireto && !semDecisor && (
            <Botao variante="secundario" disabled={!!pendente('email')} onClick={() => abrir('email')} data-pedir-email>
              {semEmail ? t('conta.entrada.pedirAdicionarEmail') : t('conta.entrada.pedirEmail')}
            </Botao>
          )}
          {!carregandoTroca && !usuarioDireto && !semDecisor && (
            <Botao variante="secundario" disabled={!!pendente('usuario')} onClick={() => abrir('usuario')} data-pedir-usuario>
              {t('conta.entrada.pedirUsuario')}
            </Botao>
          )}
        </div>
      </div>
      <Modal
        aberto={!!pedindo}
        aoFechar={() => setPedindo(null)}
        titulo={pedindo ? t(`conta.entrada.modal.${pedindo}.titulo`) : ''}
        rodape={
          <>
            <Botao variante="fantasma" onClick={() => setPedindo(null)}>
              {t('comum.cancelar')}
            </Botao>
            <Botao variante="primario" type="submit" form="pedido-acesso-form" carregando={pedir.isPending}>
              {t('conta.entrada.enviarPedido')}
            </Botao>
          </>
        }
      >
        {pedindo && (
          <form id="pedido-acesso-form" onSubmit={enviar} noValidate className="flex flex-col gap-3">
            <p className="text-sm text-tinta-2">{t(`conta.entrada.modal.${pedindo}.texto`)}</p>
            <Rotulo id="pedido-valor" rotulo={t(`conta.entrada.modal.${pedindo}.campo`)} erro={erro ?? undefined} ajuda={t(`conta.entrada.modal.${pedindo}.ajuda`)}>
              <Entrada
                id="pedido-valor"
                type={pedindo === 'email' ? 'email' : 'text'}
                autoComplete={pedindo === 'email' ? 'email' : 'username'}
                maxLength={254}
                value={valor}
                aria-invalid={!!erro || undefined}
                aria-describedby={erro ? 'pedido-valor-erro' : undefined}
                onChange={(e) => setValor(e.target.value)}
              />
            </Rotulo>
          </form>
        )}
      </Modal>
    </Cartao>
  );
}
